// frontend/src/services/batch/batchService.js
import api from '../api';
import errorService from '../error/errorService';

/**
 * Batch Service - Handles all batch-related operations
 */
class BatchService {
  /**
   * Create a new batch for a document
   * @param {string} documentId - The ID of the document
   * @param {number} batchSize - The size of the batch (number of paragraphs)
   * @returns {Promise} - Promise that resolves with the new batch
   */
  async createBatch(documentId, batchSize = 4) {
    try {
      const response = await api.post(`/documents/${documentId}/batches`, { 
        num_paragraphs: batchSize 
      });
      return response.data;
    } catch (error) {
      // Handle specific error cases
      if (error.response && error.response.status === 400 && 
          error.response.data.detail === "No more paragraphs to translate") {
        return {
          error: true,
          type: 'NO_MORE_PARAGRAPHS',
          message: 'All paragraphs have been translated'
        };
      }
      
      return errorService.handleApiError(error, 'Failed to create batch');
    }
  }

  /**
   * Get all batches for a document
   * @param {string} documentId - The ID of the document
   * @returns {Promise} - Promise that resolves with the batches
   */
  async getBatchesByDocument(documentId) {
    try {
      const response = await api.get(`/documents/${documentId}/batches`);
      
      // Sort batches by batch number to ensure correct order
      const sortedBatches = (response.data || []).sort((a, b) => a.batch_number - b.batch_number);
      return sortedBatches;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to fetch batches');
    }
  }

  /**
   * Get a specific batch by ID
   * @param {string} batchId - The ID of the batch
   * @returns {Promise} - Promise that resolves with the batch
   */
  async getBatchById(batchId) {
    try {
      const response = await api.get(`/batches/${batchId}`);
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to fetch batch');
    }
  }

  /**
   * Update batch status
   * @param {string} batchId - The ID of the batch
   * @param {string} status - The new status
   * @returns {Promise} - Promise that resolves when status is updated
   */
  async updateBatchStatus(batchId, status) {
    try {
      const response = await api.post(`/batches/${batchId}/save`, { 
        status: status
      });
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to update batch status');
    }
  }

  /**
   * Check if a document is fully translated
   * @param {string} documentId - The ID of the document
   * @param {Array} batches - Optional array of batches (to avoid extra API call)
   * @returns {Promise<boolean>} - Promise that resolves with whether the document is fully translated
   */
  async isDocumentFullyTranslated(documentId, batches = null) {
    try {
      // If batches aren't provided, fetch them
      const batchesData = batches || await this.getBatchesByDocument(documentId);
      
      // Try to create a new batch to see if there are more paragraphs
      try {
        await this.createBatch(documentId, 4);
        // If batch creation succeeds, document is not fully translated
        return false;
      } catch (err) {
        // If error is "No more paragraphs to translate", document is fully translated
        if (err.response && 
            err.response.status === 400 && 
            err.response.data.detail === "No more paragraphs to translate") {
          return true;
        }
        // If other error, check if all existing batches are translated
        return batchesData.length > 0 && 
               batchesData.every(b => b.translated_text && b.translated_text.trim() !== '');
      }
    } catch (error) {
      console.error("Error checking if document is fully translated:", error);
      // Default to false if we can't determine
      return false;
    }
  }

  /**
   * Parse and optimize the original text for processing
   * @param {string} text - The original text
   * @returns {Array} - Array of parsed paragraphs
   */
  parseParagraphs(text) {
    if (!text) return [];
    
    // Split text by blank lines (paragraphs)
    const paragraphs = text.split(/\n\s*\n/);
    
    return paragraphs.filter(p => p.trim().length > 0);
  }
}

export default new BatchService();